/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { Alert, CircularProgress } from '@mui/material';

import { DefaultAllowedSourceRange } from 'shared';

import { AlignCenter } from '../../../../../../components/AlignCenter/AlignCenter';
import { ReportableApiError } from '../../../../../../components/ReportableApiError';
import { ClusterDetailsResponsePayload } from '../../../../../../types/cluster/ClusterDetailsResponsePayload';
import { ClusterUpdateRequestBody } from '../../../../../../types/cluster/ClusterUpdateRequestBody';
import { UpdateClusterDetailsParameterIdentification } from '../../../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { fetchClusterDetails } from '../../../../../../utils/queryFns';
import { validationSchema } from './FirewallSettings.consts';
import { FirewallSettingsForm } from './FirewallSettings.Form';
import { FirewallSettingsFormValues } from './FirewallSettings.types';

type FirewallSettingsProps = {
  updateData: (data: ClusterUpdateRequestBody) => Promise<unknown>;
  identificationData: UpdateClusterDetailsParameterIdentification;
};

export const FirewallSettings = memo<FirewallSettingsProps>(props => {
  const { t } = useTranslation();

  const { updateData, identificationData } = props;

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<any>();

  const clusterDetailsQuery = useQuery([fetchClusterDetails.key, identificationData], fetchClusterDetails.fn);
  const clusterDetails = useMemo(
    () => clusterDetailsQuery.data?.payload ?? ({} as ClusterDetailsResponsePayload),
    [clusterDetailsQuery.data],
  );

  const showAclWarning = useMemo(
    () =>
      clusterDetails?.allowedSourceRanges?.length === 1 &&
      clusterDetails?.allowedSourceRanges?.[0] === DefaultAllowedSourceRange,
    [clusterDetails],
  );

  const initialValues = useMemo(
    () => ({
      allowedSourceRanges: clusterDetails.allowedSourceRanges || [],
    }),
    [clusterDetails.allowedSourceRanges],
  );

  const onSubmit = async (data: FirewallSettingsFormValues) => {
    try {
      setIsProcessing(true);
      await updateData({
        configuration: {
          allowedSourceRanges: data.allowedSourceRanges,
        },
      });
    } catch (err) {
      setError(err as any);
    } finally {
      setIsProcessing(false);
    }
  };

  return clusterDetailsQuery.isLoading ? (
    <AlignCenter>
      <CircularProgress />
    </AlignCenter>
  ) : (
    <>
      {showAclWarning ? (
        <Alert variant="outlined" severity="warning" sx={{ mb: 2 }}>
          {t('cluster:details.acl.firewall.warning')}
        </Alert>
      ) : null}
      <Formik<FirewallSettingsFormValues>
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnBlur
        validateOnChange
      >
        {formProps => <FirewallSettingsForm {...formProps} isProcessing={isProcessing} />}
      </Formik>
      {error && (
        <Alert severity="error">
          <ReportableApiError error={error} />
        </Alert>
      )}
    </>
  );
});
